// Phase 2.46.148 — In-memory TTL cache for idempotent GET endpoints.
//
// Purpose: the Data Entry page fetches the study doc, section library
// and visit-crfs on every subject/visit switch. These payloads change
// rarely (only when an admin edits the CRF builder), so re-hitting the
// backend on every navigation adds ~150-300ms of redundant round-trips.
//
// This helper wraps axios.get with a per-URL TTL cache scoped to the
// browser tab. Cache keys are `${url}?${sortedParams}` so identical
// query-param variants dedupe. In-flight requests are also deduped so
// concurrent callers on the same URL share one network call.
import axios from "axios";

const _cache = new Map();      // key -> { at: ms, data: any }
const _inflight = new Map();   // key -> Promise

function _key(url, params) {
  if (!params) return url;
  const sorted = Object.keys(params).sort()
    .map(k => `${k}=${params[k] ?? ""}`).join("&");
  return sorted ? `${url}?${sorted}` : url;
}

/**
 * Cached axios GET. Returns the axios-response-shape object
 * `{ data }` (mirrors axios.get) so callers can be a drop-in replacement.
 *
 * @param {string} url
 * @param {object} opts - `{ params, withCredentials, ttlMs }` — ttlMs
 *        defaults to 60 s.
 */
export async function cachedGet(url, opts = {}) {
  const { params, ttlMs = 60_000, ...rest } = opts;
  const k = _key(url, params);
  const now = Date.now();
  const hit = _cache.get(k);
  if (hit && (now - hit.at) < ttlMs) {
    return { data: hit.data };
  }
  const pending = _inflight.get(k);
  if (pending) return pending;

  const p = axios.get(url, { params, ...rest })
    .then(r => {
      _cache.set(k, { at: Date.now(), data: r.data });
      _inflight.delete(k);
      return { data: r.data };
    })
    .catch(err => {
      _inflight.delete(k);
      throw err;
    });
  _inflight.set(k, p);
  return p;
}

/** Bust one or all entries. Substring match: `bustCache('/api/studies/123')`
 *  clears every cache entry whose key CONTAINS that URL. Substring rather
 *  than prefix because our cache keys are full URLs (with protocol + host)
 *  while callers use path-only prefixes like `/api/subjects`. Prior to
 *  Phase 2.46.213 this used `startsWith`, which never matched and left the
 *  subject grid, dashboard, etc. serving stale data after mutations. */
export function bustCache(urlPrefix) {
  if (!urlPrefix) {
    _cache.clear();
    return;
  }
  for (const k of _cache.keys()) {
    if (k.includes(urlPrefix)) _cache.delete(k);
  }
}

// Phase 2.46.167 — Global auto-bust interceptor. Any non-GET request
// (POST / PUT / PATCH / DELETE) invalidates cache entries whose keys
// share a resource prefix with the mutation URL. Keeps stale data out
// of the UI without every mutation site having to remember to call
// bustCache().
let _interceptorInstalled = false;

/**
 * Rules for prefixes to bust when a mutation hits a URL. Add narrow
 * relationships so writes only invalidate the reads they affect.
 * Each rule: { pattern: RegExp, prefixes: (matchArr) => string[] }
 */
const _mutationRules = [
  // Any ecrf write → subject-progress + dashboard/study grids depend on it
  {
    pattern: /\/api\/ecrf(\/|$)/,
    prefixes: () => [
      "/api/dashboard/study/",
      "/api/ecrf/",
    ],
  },
  // Subject creation / update / exclusion → refresh subject lists
  {
    pattern: /\/api\/subjects(\/|\?|$)/,
    prefixes: () => [
      "/api/subjects",
      "/api/dashboard/study/",
    ],
  },
  // OCR uploads / applies → dashboard + ocr-uploads
  {
    pattern: /\/api\/(studies\/[^/]+\/ocr|ocr-uploads)/,
    prefixes: (m) => {
      const studyMatch = m.input.match(/\/api\/studies\/([^/]+)\//);
      const list = ["/api/ocr-uploads", "/api/dashboard/study/"];
      if (studyMatch) list.push(`/api/studies/${studyMatch[1]}/ocr-uploads`);
      return list;
    },
  },
  // Site edits → invalidate sites list + dashboards
  {
    pattern: /\/api\/sites(\/|\?|$)/,
    prefixes: () => ["/api/sites", "/api/dashboard/study/"],
  },
  // Investigator edits → investigators list + subject/dashboard label joins
  {
    pattern: /\/api\/(users|investigators|studies\/[^/]+\/investigators)/,
    prefixes: (m) => {
      const studyMatch = m.input.match(/\/api\/studies\/([^/]+)\//);
      const list = [
        "/api/users",
        "/api/investigators",
        "/api/dashboard/study/",
        "/api/subjects",
      ];
      if (studyMatch) list.push(`/api/studies/${studyMatch[1]}/investigators`);
      return list;
    },
  },
  // Study doc edits → invalidate study fetch
  {
    pattern: /\/api\/studies\/[^/]+(\/settings|\/investigator-fields|\/visit-list|$)/,
    prefixes: (m) => {
      const studyMatch = m.input.match(/\/api\/studies\/([^/]+)/);
      return studyMatch ? [`/api/studies/${studyMatch[1]}`] : [];
    },
  },
];

export function installCacheAutoBust(axiosInstance) {
  if (_interceptorInstalled) return;
  _interceptorInstalled = true;
  axiosInstance.interceptors.response.use((res) => {
    try {
      const method = (res.config?.method || "get").toLowerCase();
      if (method === "get" || method === "head" || method === "options") return res;
      const url = res.config?.url || "";
      for (const rule of _mutationRules) {
        const m = url.match(rule.pattern);
        if (m) {
          for (const p of rule.prefixes(m)) bustCache(p);
        }
      }
    } catch (_) { /* swallow — never break the caller */ }
    return res;
  });
}
